import type { AudioTrack } from "@veyso/core";

/**
 * Packs items into sub-rows so overlapping ones do not draw on top of one
 * another.
 *
 * Each item takes the first row it fits in, so a sequence stays on one line and
 * only genuine overlap costs height.
 */
export function packRows<T>(items: T[], span: (item: T) => { start: number; end: number }): T[][] {
  const rows: T[][] = [];
  for (const item of [...items].sort((a, b) => span(a).start - span(b).start)) {
    const row = rows.find((candidate) => span(candidate[candidate.length - 1]).end <= span(item).start + 0.001);
    if (row) row.push(item);
    else rows.push([item]);
  }
  return rows;
}

const span = (track: AudioTrack) => ({ start: track.startAt, end: track.startAt + track.duration });

/** Narration, as opposed to the recording's own audio or music. */
export const isVoice = (track: AudioTrack) => track.role.endsWith("-voice");

/**
 * The rows of the voice lane, narration first and narration together.
 *
 * Packing every audio track in one pass let the recording's own audio — which
 * spans the whole body — take the second row, so a single overlapping narration
 * line was pushed to a third row *below* it. It then read as something other
 * than narration, sitting under a different lane entirely.
 *
 * Grouping puts every narration row above every other row, so the voice stays
 * one block: one row when the lines are sequential, which is almost always, and
 * a second row directly beneath its siblings when two genuinely collide.
 */
export function voiceLaneRows(audio: AudioTrack[]): AudioTrack[][] {
  return [
    ...packRows(audio.filter(isVoice), span),
    ...packRows(
      audio.filter((track) => !isVoice(track)),
      span,
    ),
  ];
}

/**
 * Narration that is speaking while other narration speaks.
 *
 * Two voices at once is a defect in the audio, not a quirk of the drawing, so
 * the timeline marks it rather than merely laying it out. It happens when the
 * words no longer fit the recording — after a rewrite makes them longer — and
 * the builder has to pull a line back over its neighbour to keep it on screen.
 *
 * Muted tracks are ignored: a track nobody hears cannot talk over anything.
 */
export function collidingVoiceIds(audio: AudioTrack[]): Set<string> {
  const clashing = new Set<string>();
  const spoken = audio.filter((track) => isVoice(track) && track.enabled).sort((a, b) => a.startAt - b.startAt);
  for (let i = 1; i < spoken.length; i += 1) {
    if (span(spoken[i]).start < span(spoken[i - 1]).end - 0.001) {
      clashing.add(spoken[i].id);
      clashing.add(spoken[i - 1].id);
    }
  }
  return clashing;
}
